import Chart, { useChart } from 'src/components/chart';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { PresenceSegments } from 'src/components/competitive-intelligence';
import CustomTooltip from 'src/components/competitive-intelligence/components/CustomTooltip';
import Iconify from 'src/components/iconify';

/**
 * Compute the percentage of results where only competitors are present.
 * This is the complement of the total visibility rate to 100%.
 *
 * @param totalVisibilityRate The total visibility rate (brand presence).
 * @returns Percentage for the "Competitors only" segment.
 */
const calculateCompetitorsOnly = (totalVisibilityRate: number) => {
    return 100 - totalVisibilityRate;
};

/**
 * PresenceRateChart renders a compact horizontal stacked bar to visualize a presence breakdown
 * across four segments: Organic only, Solo bidder, Top ad position, and Competitors only.
 * The chart is styled as a sparkline (no axes/legend) and reveals a detailed tooltip on hover.
 *
 * Notes:
 * - Expects raw percentage values (0–100) in the input segments.
 * - Values displayed in the tooltip are rounded and clamped for readability.
 * - Colors adapt to the current MUI theme mode.
 *
 * @param data Presence distribution segments used to build the stacked bar.
 * @param data.organicOnly Percentage of results with organic presence only.
 * @param data.soloBidder Percentage of results where the brand is the only bidder.
 * @param data.topAdPosition Percentage of results where the brand holds a top ad position.
 * @param [height=40] Optional chart height in pixels.
 * @returns A React element containing the stacked bar chart with tooltip.
 */
export default function PresenceRateChart({ data, height = 40, totalVisibilityRate }: { data: PresenceSegments; height?: number; totalVisibilityRate?: number }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const colors = ['#18A155', '#C09910', '#F9DE81', 'rgba(145, 158, 171, 0.20)'];
    const visibilityBase = typeof totalVisibilityRate === 'number' ? totalVisibilityRate : 100;



    type Item = { label: string; value: number; color: string; icon: string };

    function roundToTargetSum(items: Item[], decimals = 0, totalVisibilityRate: number): Item[] {
    const factor = 10 ** decimals;
    const totalVisibilityRateUnits = Math.round(totalVisibilityRate * factor);

    // valeurs non-arrondies en "unités"
    const rawUnits = items.map(i => Math.max(0, i.value) * factor);

    // partie entière + restes
    const floored = rawUnits.map(u => Math.floor(u));
    const remainders = rawUnits.map((u, idx) => u - floored[idx]);

    let sum = floored.reduce((a, b) => a + b, 0);
    let diff = totalVisibilityRateUnits - sum; // >0: il faut ajouter, <0: il faut retirer

    // indices triés par reste (desc)
    const orderDesc = remainders
      .map((r, idx) => ({ r, idx }))
      .sort((a, b) => b.r - a.r);

    // pour retirer, on préfère ceux avec le plus petit reste (asc)
    const orderAsc = [...orderDesc].reverse();

    const adjusted = [...floored];

    if (diff > 0) {
      for (let k = 0; k < diff; k++) {
        adjusted[orderDesc[k % orderDesc.length].idx] += 1;
      }
    } else if (diff < 0) {
      diff = -diff;
      for (let k = 0; k < diff; k++) {
        const idx = orderAsc[k % orderAsc.length].idx;
        // évite de passer sous 0
        if (adjusted[idx] > 0) adjusted[idx] -= 1;
        else {
          // si celui-ci est déjà à 0, on cherche le prochain non-zéro
          const alt = orderAsc.find(o => adjusted[o.idx] > 0);
          if (alt) adjusted[alt.idx] -= 1;
        }
      }
    } 

  // reconstruit les items avec la bonne somme
  return items.map((it, idx) => ({
    ...it,
    value: adjusted[idx] / factor,
  }));
}
    const chartOptions = useChart({
        chart: {
            type: 'bar',
            stacked: true,
            parentHeightOffset: 0,
            animations: { enabled: false },
            sparkline: {
                enabled: true,
            },
            toolbar: {
                show: false,
            },
            stackType: 'normal',
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '30%',
                borderRadius: 0,
            },
        },
        stroke: {
            width: 0,
            show: false,
        },
        series: [
            { name: 'Organic only', data: [data.organicOnly] },
            { name: 'Solo bidder', data: [data.soloBidder] },
            { name: 'Top ad position', data: [data.topAdPosition] },
            { name: 'Competitors only', data: [data.competitorOnly] },
        ],
        colors,
        tooltip: {
            enabled: false,
            x: { show: false },
            marker: { show: false },
        },
        legend: {
            show: false,
        },
        grid: {
            show: false,
        },
        xaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            max: 100,
            min: 0,
        },
        yaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
    });

    // Items avec valeurs brutes (non arrondies)
    const rawItems: Item[] = [
        { label: 'Organic only', value: Math.max(0, data.organicOnly), color: colors[0], icon: 'solar:global-linear' },
        { label: 'Solo bidder', value: Math.max(0, data.soloBidder), color: colors[1], icon: 'carbon:user' },
        { label: 'Top ad position', value: Math.max(0, data.topAdPosition), color: colors[2], icon: 'carbon:user-multiple' },
        { label: 'Competitors only', value: Math.max(0, data.competitorOnly), color: colors[3], icon: 'ri:sword-line' },
    ];

    // Appliquer l'algorithme de compensation des arrondis
    const items = roundToTargetSum(rawItems, 0, 100);

    return (
        <CustomTooltip
            placement="top"
            title={
                <Stack direction="column" spacing={0.75}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Presence breakdown
                    </Typography>
                    <Stack direction="column" spacing={0.5}>
                        {items.map((it) => (
                            <Stack key={it.label} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify icon={it.icon} sx={{ width: 16, height: 16, color: it.color }} />
                                    <Typography variant="caption">{it.label}</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">{it.value}%</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            }
        >
            <Box sx={{ height, width: '100%', minWidth: 0 }}>
                <Chart
                    type="bar"
                    series={chartOptions.series}
                    options={chartOptions}
                    height={height}
                    width="100%"
                />
            </Box>
        </CustomTooltip>
    );
}


